// Plotly-free copy of the app hover builder so the LRSnapshot strip widget can reuse the
// EXACT icicle tooltip (white, metric-aware) without dragging plotly.js into the bundle.
// Source of truth remains useSunburstChart.js::createCustomData; keep in sync if that changes.
import { verdictFromLenses, BAND_STYLE } from './verdictModel';
import { METRIC_KEYS, METRIC_LABELS, fmtScore } from './Constants';
import { unitDisplayLabel } from './unitLabels';

const LENS_KEYS = ['design', 'execution', 'read'];

function asList(value) {
  if (value == null || value === false) return [];
  if (Array.isArray(value)) return value.flatMap(asList);
  if (typeof value === 'string' || typeof value === 'number') return [{ label: String(value) }];
  if (typeof value !== 'object') return [];
  if (value.label || value.move || value.rank || value.slug) return [value];
  return Object.values(value).flatMap(asList);
}

export function createCustomData(node, movie, metric, dimNotOwed = true) {
  const info = node.detailedContent;
  if (!info) return "";
  const fmt = v => fmtScore(v);   // shared: no trailing .0 on whole scores
  const wrap = (str, lim = 44) => {
    if (!str) return '';
    const words = String(str).split(' ');
    let out = '', line = '';
    words.forEach(w => { if ((line + w).length > lim) { out += line.trim() + '<br>'; line = ''; } line += w + ' '; });
    return out + line.trim();
  };

	  // Lead with the canonical node NAME ("Scene 19", "Seq 1-3") — small wheels often don't print the
	  // wedge label, so the hover is where the writer confirms which unit they're on; append the title if
	  // it adds anything beyond the name.
	  const _name = unitDisplayLabel(node, 'list') || node.label || '';
	  const _title = info.unit_title || info.title || '';   // generated orientation title wins in hover when present
	  const _norm = s => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
	  const _head = (_title && _norm(_title) !== _norm(_name)) ? (_name ? `${_name} — ${_title}` : _title) : _name;
	  let hoverText = `<b>${_head}</b>`;
  if (info.mechanism) hoverText += `  <span style="opacity:.7">(${String(info.mechanism).replace(/_/g, ' ')})</span>`;

  // ── A SPECIFIC LENS / AXIS is selected in the dropdown → the hover explains
  // THAT axis for THIS unit: its score, obligation, and the one-line note. ──
  if (metric !== 'overall') {
    const key = (METRIC_KEYS[metric] && METRIC_KEYS[metric][node.nodeType]) || metric;
    // `scores[key]` is an AXIS CELL ({score,status,obligation}) for the twelve axes, but a BARE
    // NUMBER for the aggregate keys (AnalysisController.php:2887 sets overallScore = execution as a
    // scalar). Read both shapes, or the `de` metric — which maps to overallScore, Constants.js:39 —
    // reads as undefined. Fixed 2026-08-13, landmine 76. Keep in lockstep with useSunburstChart.js.
    const rawCell = info.scores?.[key];
    const isCell = rawCell != null && typeof rawCell === 'object';
    const score = (isCell ? rawCell.score : rawCell) ?? info[key];
    let note, status;
    if (LENS_KEYS.includes(key)) {
      // a lens (Design/Execution/Read): the verdict's one-line lens summary
      note = info.prose?.verdict?.[`${key}_summary`];
    } else {
      const card = info.prose?.axes?.[key];
      note = card?.cell_note || card?.explanation;
      status = card?.status;
    }
    // Not-owed check — MUST match the wheel colouring (prepareChartData.cellOwed): a unit that
    // doesn't owe this axis (status not_owed, or only 'light' obligation) is painted neutral, so the
    // hover must say so plainly instead of showing a score the writer would mistake for a problem.
    // Owedness is an AXIS property. Lenses and aggregates are always owed, so they must never take
    // this branch — previously `de` did, and every unit's hover read "not owed here" because the
    // bare number has no `.score` and `undefined == null` is true.
    const cellObj = (LENS_KEYS.includes(key) || !isCell) ? null : rawCell;
    const NOT_OWED_ST = ['not_owed', 'na', 'not_relevant', 'not_applicable'];
    const WEAK_OBL = ['light', 'none', 'na', 'not_owed'];
    const notOwed = dimNotOwed && !!cellObj && (cellObj.score == null
      || NOT_OWED_ST.includes(cellObj.status) || WEAK_OBL.includes(cellObj.obligation));

    if (notOwed) {
      hoverText += `<br><b>${METRIC_LABELS[metric] || key}</b> <span style="opacity:.75">— not owed here</span>`;
      hoverText += `<br><em>This ${node.nodeType} isn’t really about ${(METRIC_LABELS[metric] || key).toLowerCase()}, so it isn’t scored on it — a faint colour here is normal, not a problem.</em>`;
    } else {
      hoverText += `<br><b>${METRIC_LABELS[metric] || key}</b> ${score != null ? fmt(score) + '/10' : '—'}`;
      if (status) hoverText += `  <span style="opacity:.6">· ${String(status).replace(/_/g, ' ')}</span>`;
      if (note) hoverText += `<br><em>${wrap(note)}</em>`;
      else hoverText += `<br><span style="opacity:.55">no note for this ${LENS_KEYS.includes(key) ? 'lens' : 'axis'} yet</span>`;
    }
    if (node.nodeType === 'scene' && info.page_count) hoverText += `<br><span style="opacity:.7">Pages: ${fmt(info.page_count)}</span>`;
    return hoverText;
  }

  // ── OVERALL: triage language, NOT scores — band + the three lens statuses +
  // the single biggest lever. Three lines max; no decimals (that's the click). ──
  const v = verdictFromLenses(
    { design: info.design, execution: info.execution, read: info.read },
    info.type === 'movie' ? 'script' : info.type);
  if (v.band) {
    const bs = BAND_STYLE[v.band];
    hoverText += `<br><b style="color:${bs ? bs.fg : '#0f172a'}">${String(v.label || '').toUpperCase()}</b>`;
    const w = k => (v.statuses[k] ? v.statuses[k] : '—');
    // ✓ where the group is solid, the score only where it is not (owner, 2026-10-14) — the eye goes to the problem.
    const g = k => (v.statuses[k] === 'solid' ? '✓' : (info[k] != null ? String(Math.round(info[k] * 10) / 10) : w(k)));
    hoverText += `<br>Design ${g('design')} · Execution ${g('execution')} · Involvement ${g('read')}`;
	    const reps = asList(info.prose?.repair).sort((a, b) => (a.rank || 9) - (b.rank || 9));
    let lever = reps[0] && reps[0].label;
    if (!lever) {
      const weakest = [['Design', info.design], ['Execution', info.execution], ['Involvement', info.read]]
        .filter(x => x[1] != null).sort((a, b) => a[1] - b[1])[0];
      lever = weakest ? `lift ${weakest[0]}` : null;
    }
    if (lever) hoverText += `<br><b>Biggest gain:</b> ${wrap(lever)}`;
  } else if (info.design == null) {
    hoverText += `<br><span style="opacity:.6">not analyzed yet</span>`;
  }
  if (node.nodeType === 'scene' && info.page_count) hoverText += `<br><span style="opacity:.7">Pages: ${fmt(info.page_count)}</span>`;
  return hoverText;
}
